import { Scale } from 'lucide-react'
import { DocSection } from '@/components/docs/doc-section'
import { DocPageHeader } from '@/components/docs/doc-page-header'
import { DocNav } from '@/components/docs/doc-nav'
import { MermaidDiagram } from '@/components/docs/mermaid-diagram'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const combiningAlgorithms = [
  {
    algorithm: 'deny-overrides',
    behavior: 'A single Deny wins',
    useWhen: 'Security-first: block if any rule denies',
  },
  {
    algorithm: 'permit-overrides',
    behavior: 'A single Permit wins',
    useWhen: 'Convenience-first: allow if any rule permits',
  },
  {
    algorithm: 'first-applicable',
    behavior: 'First matching rule wins',
    useWhen: 'Ordered evaluation: priority-based rules',
  },
] as const

export function XACMLPage() {
  return (
    <>
      <DocPageHeader icon={Scale} title="XACML Basics" />

      <div className="flex flex-1 flex-col gap-6 overflow-auto p-6 max-w-4xl">
        <DocSection title="What is XACML?">
          <p className="text-sm text-muted-foreground leading-relaxed">
            XACML (eXtensible Access Control Markup Language) is an OASIS standard that defines
            a language for expressing access control policies and a protocol for requesting and
            receiving authorization decisions. It provides a standardized, vendor-neutral way to
            define who can access what under which conditions.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            In practical terms, XACML lets you write a policy like "analysts can SELECT from the
            orders table only during business hours" and have that policy evaluated consistently
            across every database, application, and service in your organization. poli-x uses
            XACML 3.0 as its policy language so your access rules are portable, auditable, and
            never locked into a single vendor.
          </p>
        </DocSection>

        <Separator />

        <DocSection title="XACML Request & Response">
          <Tabs defaultValue="request" className="w-full">
            <TabsList>
              <TabsTrigger value="request">Request</TabsTrigger>
              <TabsTrigger value="response">Response</TabsTrigger>
            </TabsList>

            <TabsContent value="request" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                When an application needs to know whether a user should be allowed to perform an
                action, it sends an XACML request to the PDP. That request carries attributes
                organized into four categories:
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="default">Subject</Badge>
                <Badge variant="secondary">Resource</Badge>
                <Badge variant="outline">Action</Badge>
                <Badge variant="default">Environment</Badge>
              </div>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-muted-foreground">
                <li>
                  <span className="font-medium text-foreground">Subject</span> — Who is making
                  the request? For example, a user with department="finance" and role="analyst".
                </li>
                <li>
                  <span className="font-medium text-foreground">Resource</span> — What is being
                  accessed? For example, a database table with sensitivity="high".
                </li>
                <li>
                  <span className="font-medium text-foreground">Action</span> — What operation is
                  requested? For example, SELECT, INSERT, or DELETE.
                </li>
                <li>
                  <span className="font-medium text-foreground">Environment</span> — What is the
                  surrounding context? For example, the current time of day or network location.
                </li>
              </ul>
            </TabsContent>

            <TabsContent value="response" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                After evaluating the request against all applicable policies, the PDP returns one
                of four possible decisions:
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge className="bg-green-600 text-white hover:bg-green-700">Permit</Badge>
                <Badge className="bg-red-600 text-white hover:bg-red-700">Deny</Badge>
                <Badge className="bg-gray-500 text-white hover:bg-gray-600">Not Applicable</Badge>
                <Badge className="bg-yellow-500 text-white hover:bg-yellow-600">Indeterminate</Badge>
              </div>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-muted-foreground">
                <li>
                  <span className="font-medium text-foreground">Permit</span> — The request is
                  allowed. The user can proceed with the action.
                </li>
                <li>
                  <span className="font-medium text-foreground">Deny</span> — The request is
                  explicitly forbidden by at least one policy.
                </li>
                <li>
                  <span className="font-medium text-foreground">Not Applicable</span> — No policy
                  matched the request. The PEP typically treats this as a deny.
                </li>
                <li>
                  <span className="font-medium text-foreground">Indeterminate</span> — An error
                  occurred during evaluation (e.g., a required attribute was missing).
                </li>
              </ul>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Alongside the decision, the PDP can return <span className="font-medium text-foreground">obligations</span> (actions
                the PEP must perform, like logging the access) and <span className="font-medium text-foreground">advice</span> (optional
                recommendations the PEP may follow, like masking the SSN column for analysts).
              </p>
            </TabsContent>
          </Tabs>
        </DocSection>

        <MermaidDiagram
          src="/docs/diagrams/xacml-lifecycle.svg"
          alt="XACML request response lifecycle sequence diagram"
          caption="The XACML request/response lifecycle between application, PEP, and PDP"
        />

        <Separator />

        <DocSection title="Combining Algorithms">
          <p className="text-sm text-muted-foreground leading-relaxed">
            When multiple rules or policies apply to the same request, the combining algorithm
            determines the final result. Think of it as a tie-breaking strategy — it tells the
            PDP how to reconcile conflicting decisions.
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Algorithm</TableHead>
                <TableHead>Behavior</TableHead>
                <TableHead>Use When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {combiningAlgorithms.map((row) => (
                <TableRow key={row.algorithm}>
                  <TableCell className="font-mono text-xs">{row.algorithm}</TableCell>
                  <TableCell>{row.behavior}</TableCell>
                  <TableCell className="text-muted-foreground">{row.useWhen}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DocSection>

        <DocNav
          prev={{ title: 'What is ABAC?', url: '/docs/abac' }}
          next={{ title: 'Attributes', url: '/docs/attributes' }}
        />
      </div>
    </>
  )
}
